import React from 'react';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber } from '../types';

interface TopUsersTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = ['top-users'];

const UserRow = ({ user, metric, metricLabel }: { user: any; metric: number; metricLabel: string }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
    <div className="flex items-center gap-3">
      {user.ProfileImageURL ? (
        <img src={user.ProfileImageURL} alt="" className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] flex items-center justify-center text-white text-xs font-bold">
          {(user.UserName || user.Name || user.userName || user.name || '?')[0]?.toUpperCase() || '?'}
        </div>
      )}
      <div>
        <p className="text-white text-sm font-medium">{user.Name || user.name || user.UserName || user.userName || 'Anonymous'}</p>
        <p className="text-gray-500 text-xs">@{user.UserName || user.userName || 'n/a'}</p>
      </div>
    </div>
    <div className="text-right">
      <p className="text-white font-semibold">{formatNumber(metric)}</p>
      <p className="text-gray-500 text-xs">{metricLabel}</p>
    </div>
  </div>
);

const VideoRow = ({ item, metricLabel }: { item: any; metricLabel: string }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
    <p className="text-gray-300 text-sm truncate max-w-[200px]">{item.videoId || 'N/A'}</p>
    <div className="text-right">
      <p className="text-white font-semibold">{formatNumber(item.count)}</p>
      <p className="text-gray-500 text-xs">{metricLabel}</p>
    </div>
  </div>
);

const TopUsersTab: React.FC<TopUsersTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const topData = data['top-users'] || {};
  const topFollowed = topData.topFollowed || [];
  const topUploaders = topData.topVideoUploaders || [];
  const topWatched = topData.topWatched || [];
  const topLiked = topData.topLiked || [];
  const topCommented = topData.topCommented || [];
  const topShared = topData.topShared || [];
  const topSaved = topData.topSaved || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Most Followed Users (Top 20)">
          <div className="max-h-[500px] overflow-y-auto space-y-0">
            {topFollowed.map((u: any, i: number) => (
              <UserRow key={i} user={u} metric={u.FollowersCount || 0} metricLabel="followers" />
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Top Video Uploaders (Top 20)">
          <div className="max-h-[500px] overflow-y-auto space-y-0">
            {topUploaders.map((u: any, i: number) => (
              <UserRow key={i} user={u} metric={u.videoCount || 0} metricLabel="videos" />
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Top Watched Videos">
          <div className="max-h-[400px] overflow-y-auto">
            {topWatched.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="watches" />)}
          </div>
        </ChartCard>

        <ChartCard title="Top Liked Videos">
          <div className="max-h-[400px] overflow-y-auto">
            {topLiked.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="likes" />)}
          </div>
        </ChartCard>

        <ChartCard title="Top Commented Videos">
          <div className="max-h-[400px] overflow-y-auto">
            {topCommented.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="comments" />)}
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top Shared Videos">
          <div className="max-h-[400px] overflow-y-auto">
            {topShared.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="shares" />)}
          </div>
        </ChartCard>

        <ChartCard title="Top Saved Videos">
          <div className="max-h-[400px] overflow-y-auto">
            {topSaved.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="saves" />)}
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default TopUsersTab;
